import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import useAsyncValue from '../../../../hooks/useAsyncValue';
import { patch } from '../../../../redux/api';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import PropagateModal from '../../shared/SettingsPropagateModal';
import type { ProjectSnippetSettings } from '../../../../../shared/settings/project/snippets';
import type { FailedFetchResponse } from '../../../../helpers/errors';

type PropagateGeneralProjectSnippetSSettings = Pick<ProjectSnippetSettings, 'projectDefaultSnippetAutoRejectMatchPercentageThreshold'>;

type Props = {
  //  If the modal is open or not
  isOpen: boolean;
  //  We need the current state of the settings so that we can
  //  alter the text on the checkboxes so that it makes sense.
  settings: PropagateGeneralProjectSnippetSSettings;
  //  Hide the modal
  onHide: () => void;
  // Endpoint for propagating settings
  endpoint: string;
};

//  All the propagate checkboxes are disabled by default
const resetCheckboxState = {
  projectDefaultSnippetAutoRejectMatchPercentageThreshold: false,
};

/**
 * Hook for managing the state of the checkboxes and making the PATCH request.
 */
const usePropagateGeneralProjectSnippetSettings = ({
  isOpen,
  onHide,
  endpoint,
}: Omit<Props, 'settings'>) => {
  const addToast = useAddToast();
  //  Void here because we don't really care about the stored value, we're just using this to track the status of the PATCH request.
  const asyncValue = useAsyncValue<void>();
  const [checkboxes, setCheckboxes] = useState<Record<keyof PropagateGeneralProjectSnippetSSettings, boolean>>(
    resetCheckboxState,
  );

  //  Reset the checkboxes when the modal closes.
  //  We always want the user to select which options should propagate,
  //  which is why none should be checked when the user opens the modal
  useEffect(() => {
    setCheckboxes(resetCheckboxState);
  }, [isOpen]);

  const onSavePropagateSettings = useCallback(async () => {
    try {
      asyncValue.setPending();
      await patch<Array<keyof PropagateGeneralProjectSnippetSSettings>>(
        endpoint,
        _.compact(
          Object.entries(checkboxes)
            .map(([key, value]) => (value ? (key as keyof PropagateGeneralProjectSnippetSSettings) : null)),
        ),
      );
      asyncValue.setResolve();
      //  Show toast and hide the modal after we successfully propagate.
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      asyncValue.setReject(
        errorMessagesListFromResponse(error as FailedFetchResponse),
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: check if this is correct.
  }, [addToast, asyncValue, checkboxes, onHide]);

  return {
    checkboxes,
    setCheckboxes,
    onSavePropagateSettings,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    isSaveButtonDisabled: asyncValue.isLoading || _.isEqual(checkboxes, resetCheckboxState),
  };
};

/**
 * PropagateGeneralProjectSnippetSettingsModal is a modal for displaying the propagation
 * options and making the PATCH request to propagate the settings.
 */
const PropagateGeneralProjectSnippetSettingsModal: VoidFunctionComponent<Props> = ({
  isOpen,
  settings,
  onHide,
  endpoint,
}) => {
  const {
    errors,
    isLoading,
    checkboxes,
    setCheckboxes,
    isSaveButtonDisabled,
    onSavePropagateSettings,
  } = usePropagateGeneralProjectSnippetSettings({
    isOpen,
    onHide,
    endpoint,
  });

  const label = settings.projectDefaultSnippetAutoRejectMatchPercentageThreshold === null
    ? 'Remove the snippet auto-reject match percentage threshold from all projects'
    : `Set the snippet auto-reject match percentage threshold to ${settings.projectDefaultSnippetAutoRejectMatchPercentageThreshold}% for all projects`;
  return (
    <PropagateModal
      settingsCategory="snippets"
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={onSavePropagateSettings}
      isSaveButtonDisabled={isSaveButtonDisabled}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label={label}
        isChecked={checkboxes.projectDefaultSnippetAutoRejectMatchPercentageThreshold}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          projectDefaultSnippetAutoRejectMatchPercentageThreshold: isChecked,
        }))}
        isRippleDisabled
      />
    </PropagateModal>
  );
};

export default PropagateGeneralProjectSnippetSettingsModal;
